// ADR 5.9.26i §3 — the i18next instance.
//
// Resources come from `src/locales/generated/`, which is gitignored and
// produced by `scripts/build-locales.mjs` at predev / prebuild / pretest (and
// by the `make` prerequisites, so neither entry point can skip it).
//
// The glob is LAZY, and the difference is not academic: 31 catalogues of ~1,600
// keys is ~4 MB of JSON, and an eager glob puts every one of them in the
// entry chunk for a visitor who will read exactly one. Lazy costs nothing in
// return, because `initI18n` is awaited before the first render either way —
// so there is no frame of raw catalogue keys, and on an RTL locale no frame of
// left-to-right layout that then flips.
//
// ADR 8.10.26u §3 — two namespaces. `app` is loaded at boot; `console` (the
// builder, OAuth-client and webhook copy, split by `scripts/localeNamespaces.mjs`)
// is added by `ensureNamespace` from a console route's `lazy`. `fallbackNS`
// lets every console call site keep its bare `t('builder_…')`. Load order is
// the invariant: react-i18next does not re-render on a bundle being added
// (`bindI18nStore` is `''`), so the namespace must arrive before the route
// commits — never fire-and-forget from a component effect.

import i18next, { type i18n as I18n } from 'i18next'
import { initReactI18next } from 'react-i18next'

import { readStoredLocale, recordLaunch } from './localeStore'
import { browserLocales, chooseLocale, type SupportedLocale } from './resolve'
import { applyDocumentDirection } from './rtl'

export const NAMESPACE = 'app'
export const CONSOLE_NAMESPACE = 'console'
const FALLBACK: SupportedLocale = 'en'

type Catalogue = Record<string, string>

const catalogues = import.meta.glob<Catalogue>('../locales/generated/*.json', {
  import: 'default',
})

// `*` does not cross a `/`, so the glob above never picks these up.
const consoleCatalogues = import.meta.glob<Catalogue>('../locales/generated/console/*.json', {
  import: 'default',
})

/** Load one generated catalogue by locale (and namespace, `app` by default). */
export async function loadCatalogue(locale: string, ns: string = NAMESPACE): Promise<Catalogue> {
  const load =
    ns === CONSOLE_NAMESPACE
      ? consoleCatalogues[`../locales/generated/console/${locale}.json`]
      : ns === NAMESPACE
        ? catalogues[`../locales/generated/${locale}.json`]
        : undefined
  if (!load) throw new Error(`no generated "${ns}" catalogue for locale "${locale}"`)
  return load()
}

// The namespaces beyond `app` this session has asked for, so a language switch
// loads them for the new locale too (`appLanguage.ts` `ensureCatalogue`).
const wanted = new Set<string>()

/** The namespaces `ensureNamespace` has been asked for, in request order. */
export function wantedNamespaces(): readonly string[] {
  return [...wanted]
}

/** Test-only: forget every wanted namespace, so tests do not depend on order. */
export function resetWantedNamespaces(): void {
  wanted.clear()
}

async function addMissing(i18n: I18n, ns: string, locales: readonly string[]): Promise<void> {
  const missing = [...new Set(locales)].filter((locale) => !i18n.hasResourceBundle(locale, ns))
  const loaded = await Promise.all(missing.map((locale) => loadCatalogue(locale, ns)))
  missing.forEach((locale, i) => i18n.addResourceBundle(locale, ns, loaded[i]))
}

/**
 * Add `ns` for the current language and `en`, unless present. `ns` is wanted
 * before the first await, and the language is re-read afterwards, so a switch
 * that lands meanwhile still ends with `ns` loaded for the new language.
 */
export async function ensureNamespace(i18n: I18n, ns: string): Promise<void> {
  wanted.add(ns)
  // `main.tsx` awaits `initI18n` before the router exists, so this only meets
  // an uninitialised instance in a test that mounts routes under its own
  // instance; there is no language to load for and no store to write to.
  if (!i18n.isInitialized) return
  const first = i18n.language
  await addMissing(i18n, ns, [first, FALLBACK])
  if (i18n.language !== first) await addMissing(i18n, ns, [i18n.language])
}

/** `{ ar: { app: {…} }, en: { app: {…} } }` — the chosen locale plus the fallback. */
export async function loadResources(
  locale: SupportedLocale,
): Promise<Record<string, Record<string, Catalogue>>> {
  const locales = locale === FALLBACK ? [FALLBACK] : [locale, FALLBACK]
  const loaded = await Promise.all(locales.map((name) => loadCatalogue(name)))
  return Object.fromEntries(locales.map((name, i) => [name, { [NAMESPACE]: loaded[i] }]))
}

/**
 * Initialise i18next for the locale this browser asked for, and set the
 * document's direction to match.
 */
export async function initI18n(
  search: string = window.location.search,
  doc: Document = document,
): Promise<I18n> {
  // ADR 30.9.26d §2 — `?lng=` > stored choice > browser > `en`.
  const stored = readStoredLocale()
  const launch = chooseLocale(search, stored, browserLocales())
  const locale: SupportedLocale = launch.locale
  await i18next.use(initReactI18next).init({
    resources: await loadResources(locale),
    lng: locale,
    fallbackLng: FALLBACK,
    ns: [NAMESPACE],
    defaultNS: NAMESPACE,
    // ADR 8.10.26u §3 — a key not in `app` is looked up in `console`, so no
    // console call site names a namespace.
    fallbackNS: CONSOLE_NAMESPACE,
    // The catalogue is `{{0}}`-shaped, not `<b>`-shaped: iOS and Android both
    // render these strings as plain text, so escaping here would only mangle
    // apostrophes the translators already wrote.
    interpolation: { escapeValue: false },
  })
  applyDocumentDirection(locale, doc)
  recordLaunch(launch, stored)
  return i18next
}

export { i18next }
